import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import type { Tone } from "@/features/utils/colors";
import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium leading-4 transition-colors focus:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/40",
  {
    variants: {
      variant: {
        default: "border-transparent bg-info-soft text-info-foreground ",
        secondary: "border-transparent bg-secondary text-secondary-foreground ",
        destructive:
          "border-transparent bg-danger-soft text-danger-foreground ",
        outline: "text-foreground bg-card",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

/**
 * A status is a `Tone`, drawn on the status tokens. Yellow and orange are both
 * the warning token: the names are kept apart because callers say which one they
 * mean, not because they render differently.
 */
const TONE_CLASSES: Record<Tone, string> = {
  blue: "bg-info-soft border-transparent text-info-foreground",
  green: "bg-success-soft border-transparent text-success-foreground",
  red: "bg-danger-soft border-transparent text-danger-foreground",
  yellow: "bg-warning-soft border-transparent text-warning-foreground",
  orange: "bg-warning-soft border-transparent text-warning-foreground",
  purple: "bg-purple/12 border-transparent text-purple dark:text-purple",
  pink: "bg-pink/12 border-transparent text-pink dark:text-pink",
  gray: "bg-secondary border-transparent text-muted-foreground",
};

export interface BadgeProps
  extends
    Omit<React.HTMLAttributes<HTMLDivElement>, "color">,
    VariantProps<typeof badgeVariants> {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  /**
   * A `Tone` for a state; a hex colour only for a value that carries no meaning
   * of its own (a tenant's select option, hashed with `getColorForString`).
   */
  color?: Tone | (string & {});
}

function Badge({
  className,
  variant,
  icon,
  color,
  style,
  ...props
}: BadgeProps) {
  const cnColor = color ? TONE_CLASSES[color as Tone] : undefined;

  return (
    <div
      className={cn(badgeVariants({ variant }), className, cnColor)}
      style={{
        ...(style || {}),
        ...(color && !cnColor
          ? {
              backgroundColor: color + "20",
              color: color,
              borderColor: color + "40",
            }
          : {}),
      }}
      {...props}
    >
      {icon && React.createElement(icon, { className: "h-3 w-3 -ml-0.5" })}
      {props.children}
    </div>
  );
}

export { Badge, badgeVariants };
